import React, { useEffect, useRef, useState } from 'react';

import { pad } from '../draws';

const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const CYCLES = 4; // full turns of each reel before it lands
const STAGGER_MS = 700; // each reel stops a little after the one on its left
const ARM_MS = 50; // the reels are drawn at the top first, so the transition has somewhere to go
const DEFAULT_DURATION = 6000;

// The strip behind a reel: the digits, several turns of them, ending on the one that lands
const stripFor = (digit) => [...Array(CYCLES).fill(DIGITS).flat(), ...DIGITS.slice(0, Number(digit) + 1)];

/**
 * The jackpot machine: three reels that spin when the admin launches a draw and stop, one
 * after the other, on the number that goes up on stage. Everyone wears a numbered bandana,
 * so the number is all the app needs to show.
 */
const Jackpot = ({ jackpot }) => {
  const [phase, setPhase] = useState('idle'); // idle | spinning | revealed
  const [armed, setArmed] = useState(false); // the reels sit at their landing position
  const drawId = useRef(null);
  const timers = useRef([]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const duration = jackpot?.duration || DEFAULT_DURATION;
  const digits = (jackpot?.number || '000').split('');

  useEffect(() => {
    if (!jackpot || jackpot.id === drawId.current) return;
    drawId.current = jackpot.id;
    timers.current.forEach(clearTimeout);
    timers.current = [];

    // A number recovered from the history (a late joiner) is shown without animating
    if (!jackpot.live) {
      setPhase('revealed');
      setArmed(true);
      return;
    }

    setPhase('spinning');
    setArmed(false);
    timers.current.push(setTimeout(() => setArmed(true), ARM_MS));
    timers.current.push(setTimeout(() => setPhase('revealed'), duration));
  }, [jackpot, duration]);

  const spinning = phase === 'spinning';
  const reelDuration = (index) => duration - (digits.length - 1 - index) * STAGGER_MS;

  return (
    <section className='jackpot'>
      <h2 className='stage__title'>Jackpot</h2>

      {/* A new draw remounts the reels, so they start again from the top */}
      <div key={jackpot?.id || 'idle'} className={`jackpot__machine${phase === 'revealed' ? ' jackpot__machine--hit' : ''}`}>
        {digits.map((digit, index) => {
          const strip = phase === 'idle' ? [digit] : stripFor(digit);
          const position = armed ? strip.length - 1 : 0;
          return (
            <div key={index} className='jackpot__reel'>
              <div
                className='jackpot__strip'
                style={{
                  transform: `translateY(calc(${-position} * var(--digit)))`,
                  transition: spinning && armed ? `transform ${reelDuration(index)}ms cubic-bezier(0.12, 0.8, 0.2, 1)` : 'none',
                }}
              >
                {strip.map((value, at) => (
                  <span key={at} className='jackpot__digit'>
                    {value}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {phase === 'revealed' && jackpot ? (
        <div className='stage__result'>
          <p className='stage__winner'>🎰 Salió el {jackpot.number}</p>
          <p className='stage__caption'>Si tenés ese número, subí al escenario</p>
        </div>
      ) : (
        <p className='stage__caption'>
          {spinning
            ? `Sorteando entre el ${pad(jackpot.from)} y el ${pad(jackpot.to)}…`
            : 'El sorteo arranca desde el escenario. Tené a mano tu número.'}
        </p>
      )}
    </section>
  );
};

export default Jackpot;
